import React, { useCallback, useEffect, useState } from 'react';

import StandardDrawer from 'components/Drawers/StandardDrawer';
import type {
  RootModalDrawerOptions,
  StandardDrawerPlane,
  StandardDrawerVariant,
} from 'components/Drawers/standardDrawerContract';

import ModalRenderer from './ModalRenderer';
import type { RootModalRecord } from './types';
import { useRemoveRootModalRecord } from './useRootModalUrlDialogs';

type RootModalDrawerRendererProps = {
  drawerOptions: RootModalDrawerOptions;
  modal: RootModalRecord;
  plane?: StandardDrawerPlane;
  variant: StandardDrawerVariant;
};

function RootModalDrawerRenderer({ drawerOptions, modal, plane, variant }: RootModalDrawerRendererProps) {
  const removeRootModalRecord = useRemoveRootModalRecord();
  const [open, setOpen] = useState<boolean>(modal.visible);
  const [isDirty, setIsDirty] = useState<boolean>(Boolean(drawerOptions.isDirty));
  useEffect(() => {
    setOpen(modal.visible);
  }, [modal.visible]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Root UI removal is delayed until the drawer exit finishes so MUI keeps the
  // child mounted long enough for close animations to run. A drawer whose content failed to load never ran that
  // content's own close, so this also drops a URL-backed record from the URL.
  const handleExited = useCallback(() => {
    removeRootModalRecord(modal.id);
  }, [modal.id, removeRootModalRecord]);

  return (
    <StandardDrawer
      closeTransition={drawerOptions.closeTransition}
      dirtyConfirmation={drawerOptions.dirtyConfirmation}
      isDirty={isDirty}
      onClose={handleClose}
      onExited={handleExited}
      open={open}
      plane={plane}
      showScrim={drawerOptions.showScrim}
      variant={variant}
      width={drawerOptions.width}
    >
      <ModalRenderer
        modal={modal}
        onRootModalDrawerDirtyChange={setIsDirty}
        dialogType={modal.type}
        onRootModalDrawerClose={handleClose}
        rootModalDrawer={drawerOptions}
      />
    </StandardDrawer>
  );
}

export default React.memo(RootModalDrawerRenderer);
